/** Own-account panels: password, email, language and calendar feed. */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LOCALES, type Locale } from "@offertli/shared";
import { Button } from "../../components/ui/Button.js";
import { Select as Dropdown } from "@primereact/ui/select";
import { Field } from "../../components/ui/Field.js";
import { InputText as Input } from "@primereact/ui/inputtext";
import { Dialog as Modal } from "@primereact/ui/dialog";
import { Menu } from "@primereact/ui/menu";
import { apiPost } from "../../lib/api.js";
import { useUpdateLocale } from "../../lib/auth.js";
import { MutationError, Section } from "../shared.js";

export function ChangePasswordPanel() {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [saved, setSaved] = useState(false);
  const change = useMutation({
    mutationFn: () =>
      apiPost("/auth/change-password", {
        currentPassword,
        newPassword,
      }),
    onSuccess: () => {
      setCurrentPassword("");
      setNewPassword("");
      setSaved(true);
    },
  });
  return (
    <Section className="mt-5" title={t("employees.changePassword")}>
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          setSaved(false);
          change.mutate();
        }}
      >
        <Field label={t("auth.currentPassword")} required>
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentPassword(event.target.value)
            }
          />
        </Field>
        <Field label={t("auth.newPassword")} required>
          <Input
            type="password"
            autoComplete="new-password"
            required
            minLength={12}
            value={newPassword}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setNewPassword(event.target.value)
            }
          />
        </Field>
        <MutationError error={change.error} />
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" loading={change.isPending || undefined}>
            {t("employees.changePassword")}
          </Button>
          {saved ? (
            <span className="text-sm font-medium text-success">
              {t("common.saved")}
            </span>
          ) : null}
        </div>
      </form>
    </Section>
  );
}
export function SecurityPanel() {
  return (
    <>
      <ChangePasswordPanel />
      <EmailChangePanel />
    </>
  );
}
export function EmailChangePanel() {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [sent, setSent] = useState(false);
  const change = useMutation({
    mutationFn: () =>
      apiPost("/auth/email-change/request", {
        currentPassword,
        newEmail,
      }),
    onSuccess: () => {
      setCurrentPassword("");
      setNewEmail("");
      setSent(true);
    },
  });
  return (
    <Section className="mt-5" title={t("employees.changeEmail")}>
      <form
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event) => {
          event.preventDefault();
          setSent(false);
          change.mutate();
        }}
      >
        <Field label={t("employees.currentPassword")} required>
          <Input
            type="password"
            autoComplete="current-password"
            required
            value={currentPassword}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCurrentPassword(event.target.value)
            }
          />
        </Field>
        <Field label={t("employees.newEmail")} required>
          <Input
            type="email"
            autoComplete="email"
            required
            value={newEmail}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setNewEmail(event.target.value)
            }
          />
        </Field>
        <MutationError error={change.error} />
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" loading={change.isPending || undefined}>
            {t("employees.requestEmailChange")}
          </Button>
          {sent ? (
            <span className="text-sm font-medium text-success">
              {t("employees.emailChangeSent")}
            </span>
          ) : null}
        </div>
      </form>
    </Section>
  );
}
export function LanguagePanel() {
  const { t, i18n } = useTranslation();
  const updateLocale = useUpdateLocale();
  const current = SUPPORTED_LOCALES.includes(
    i18n.resolvedLanguage as (typeof SUPPORTED_LOCALES)[number],
  )
    ? i18n.resolvedLanguage
    : "en";
  return (
    <Section title={t("employees.language")}>
      <p className="mb-4 text-sm text-app-muted">
        {t("employees.languageHelp")}
      </p>
      <Field label={t("employees.language")}>
        <Dropdown.Root
          value={current}
          options={SUPPORTED_LOCALES.map((value) => ({
            value,
            label: t(`locale.${value}`),
          }))}
          optionLabel="label"
          optionValue="value"
          onValueChange={(event: { value: unknown }) =>
            updateLocale.mutate(event.value as Locale)
          }
          disabled={updateLocale.isPending}
          fluid
        >
          <Dropdown.Trigger>
            <Dropdown.Value />
            <Dropdown.Indicator className="pi pi-chevron-down" />
          </Dropdown.Trigger>
          <Dropdown.Portal>
            <Dropdown.Positioner>
              <Dropdown.Popup>
                <Dropdown.List />
              </Dropdown.Popup>
            </Dropdown.Positioner>
          </Dropdown.Portal>
        </Dropdown.Root>
      </Field>
      <MutationError error={updateLocale.error} />
    </Section>
  );
}
export function CalendarFeedPanel() {
  const { t } = useTranslation();
  const [token, setToken] = useState("");
  const [version, setVersion] = useState(1);
  const [open, setOpen] = useState(false);
  const create = useMutation({
    mutationFn: () =>
      apiPost<{
        data?: {
          token?: string;
          version?: number;
        };
      }>("/calendar-feed/token"),
    onSuccess: (response) => {
      setToken(response.data?.token ?? "");
      setVersion(response.data?.version ?? 1);
    },
  });
  const rotate = useMutation({
    mutationFn: () =>
      apiPost<{
        data?: {
          token?: string;
          version?: number;
        };
      }>("/calendar-feed/token/rotate", {
        version,
      }),
    onSuccess: (response) => {
      setToken(response.data?.token ?? "");
      setVersion(response.data?.version ?? version + 1);
    },
  });
  const revoke = useMutation({
    mutationFn: () =>
      apiPost("/calendar-feed/token/revoke", {
        version,
      }),
    onSuccess: () => setToken(""),
  });
  const feedUrl =
    token && typeof window !== "undefined"
      ? `${window.location.origin}/api/v1/calendar/${token}.ics`
      : "";
  return (
    <>
      <div className="flex items-center justify-end my-4">
        <Menu.Root>
          <Menu.Trigger
            as={Button}
            type="button"
            variant="outlined"
            severity="secondary"
            iconOnly
            aria-label={t("employees.calendarFeed")}
          >
            <i className="pi pi-ellipsis-v" />
          </Menu.Trigger>
          <Menu.Portal>
            <Menu.Positioner sideOffset={4}>
              <Menu.Popup>
                <Menu.List>
                  <Menu.Group>
                    <Menu.Label>{t("employees.calendarFeed")}</Menu.Label>
                    <Menu.Item onSelect={() => setOpen(true)}>
                      <i className="pi pi-calendar mr-2" />
                      {t("employees.createCalendarFeed")}
                    </Menu.Item>
                  </Menu.Group>
                </Menu.List>
              </Menu.Popup>
            </Menu.Positioner>
          </Menu.Portal>
        </Menu.Root>
        <Modal.Root
          open={open}
          onOpenChange={(event: { value: boolean | undefined }) =>
            setOpen(Boolean(event.value))
          }
        >
          <Modal.Portal>
            <Modal.Backdrop />
            <Modal.Positioner>
              <Modal.Popup>
                <Modal.Header>
                  <Modal.Title>{t("employees.calendarFeed")}</Modal.Title>
                  <Modal.HeaderActions>
                    <Modal.Close className="pi pi-times cursor-pointer" />
                  </Modal.HeaderActions>
                </Modal.Header>
                <Modal.Content>
                  <p className="mb-4 text-sm text-app-muted">
                    {t("employees.calendarFeedHelp")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={() => create.mutate()}
                      loading={create.isPending || undefined}
                    >
                      {t("employees.createCalendarFeed")}
                    </Button>
                    <Button
                      variant="outlined"
                      severity="secondary"
                      onClick={() => rotate.mutate()}
                      loading={rotate.isPending || undefined}
                      disabled={!token}
                    >
                      {t("employees.rotateCalendarFeed")}
                    </Button>
                    <Button
                      variant="outlined"
                      severity="secondary"
                      onClick={() => revoke.mutate()}
                      loading={revoke.isPending || undefined}
                      disabled={!token}
                    >
                      {t("employees.revokeCalendarFeed")}
                    </Button>
                  </div>
                  <MutationError
                    error={create.error ?? rotate.error ?? revoke.error}
                  />
                  {feedUrl ? (
                    <Field
                      className="mt-4"
                      label={t("employees.calendarFeedUrl")}
                    >
                      <Input readOnly value={feedUrl} />
                    </Field>
                  ) : null}
                </Modal.Content>
              </Modal.Popup>
            </Modal.Positioner>
          </Modal.Portal>
        </Modal.Root>
      </div>
    </>
  );
}
